
import React from 'react';
import PropTypes from 'prop-types';

const Button = ({
    children,
    onClick,
    type = 'button',
    variant = 'primary',
    size = 'medium',
    className = '',
    disabled = false,
    style: userStyle = {},
    ...props
}) => {

    const baseStyles = {
        border: 'none',
        borderRadius: '8px',
        fontWeight: 'bold',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled ? 0.6 : 1,
        fontFamily: 'inherit'
    };

    const variants = {
        primary: {
            background: 'linear-gradient(90deg, #d90429, #ff0055)',
            color: '#fff',
        },
        secondary: {
            background: '#333',
            color: '#fff',
        },
        danger: {
            background: '#d90429',
            color: '#fff',
        },
        outline: {
            background: 'transparent',
            border: '1px solid #444',
            color: '#aaa',
        },
        transparent: {
            background: 'transparent',
            color: '#fff',
        }
    };

    const sizes = {
        small: {
            padding: '10px 20px',
            fontSize: '1rem',
        },
        medium: {
            padding: '16px 32px',
            fontSize: '1.1rem',
        },
        large: {
            padding: '20px 40px',
            fontSize: '1.25rem',
        }
    };

    const style = {
        ...baseStyles,
        ...variants[variant],
        ...sizes[size],
        textDecoration: 'none', // Ensure links don't have underline
        ...userStyle
    };

    if (props.href) {
        return (
            <a
                className={`ui-button ${className}`}
                style={style}
                onClick={onClick} // Pass onClick to <a> as well
                onMouseEnter={(e) => {
                    if (!disabled) {
                        e.currentTarget.style.transform = 'scale(1.02)';
                        if (variant === 'outline') {
                            e.currentTarget.style.borderColor = '#666';
                            e.currentTarget.style.color = '#fff';
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        }
                    }
                }}
                onMouseLeave={(e) => {
                    if (!disabled) {
                        e.currentTarget.style.transform = 'scale(1)';
                        if (variant === 'outline') {
                            e.currentTarget.style.borderColor = '#444';
                            e.currentTarget.style.color = '#aaa';
                            e.currentTarget.style.background = 'transparent';
                        }
                    }
                }}
                {...props}
            >
                {children}
            </a>
        );
    }

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className={`ui-button ${className}`}
            style={style}
            onMouseEnter={(e) => {
                if (!disabled) {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    if (variant === 'outline') {
                        e.currentTarget.style.borderColor = '#666';
                        e.currentTarget.style.color = '#fff';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                }
            }}
            onMouseLeave={(e) => {
                if (!disabled) {
                    e.currentTarget.style.transform = 'scale(1)';
                    if (variant === 'outline') {
                        e.currentTarget.style.borderColor = '#444';
                        e.currentTarget.style.color = '#aaa';
                        e.currentTarget.style.background = 'transparent';
                    }
                }
            }}
            {...props}
        >
            {children}
        </button>
    );
};

Button.propTypes = {
    children: PropTypes.node.isRequired,
    onClick: PropTypes.func,
    type: PropTypes.oneOf(['button', 'submit', 'reset']),
    variant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'outline', 'transparent']),
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    className: PropTypes.string,
    disabled: PropTypes.bool,
    href: PropTypes.string,
};

export { Button };
